import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Icon } from "@iconify-icon/react";
import { Button, buttonVariants } from "../../ui/button";
import { Label } from "../../ui/label";
import { cn } from "../../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import MessageTile from "../../../HelperComponents/MessageTile";
import {
  getPayStubCheckDates,
  getPayStubCheckNumbers,
  requestTaxDocument,
  CheckDatesResponse,
  CheckNumbersResponse,
} from "../../../Service/Services";
import { downloadBlob } from "../../../Utility/Utility";

const formatCheckDate = (checkDate: string): string =>
  moment(checkDate, "YYYY-MM-DD").format("MMM D, YYYY");

const MyPayStubs: React.FC = () => {
  const [checkDates, setCheckDates] = useState<string[]>([]);
  const [isLoadingDates, setIsLoadingDates] = useState(true);
  const [selectedCheckDate, setSelectedCheckDate] = useState<string | null>(
    null,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [checkNumbers, setCheckNumbers] = useState<string[]>([]);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);
  const [selectedCheckNumber, setSelectedCheckNumber] = useState<string | null>(
    null,
  );

  const [isRequesting, setIsRequesting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">(
    "success",
  );

  // Updated synchronously at selection time so an in-flight check-number lookup
  // for a since-superseded date can be detected and discarded when it resolves.
  const activeCheckDateRef = useRef<string | null>(null);

  const fetchCheckNumbers = async (checkDate: string) => {
    setIsLoadingNumbers(true);
    setCheckNumbers([]);
    setSelectedCheckNumber(null);

    try {
      const response = await getPayStubCheckNumbers(checkDate);
      const data = response.data as CheckNumbersResponse;

      if (activeCheckDateRef.current !== data.checkDate) return;

      const numbers = data.checkNumbers || [];
      setCheckNumbers(numbers);
      if (numbers.length === 1) {
        setSelectedCheckNumber(numbers[0]);
      }
    } catch (error) {
      if (activeCheckDateRef.current !== checkDate) return;
      setMessageType("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Failed to load check numbers for the selected pay date.",
      );
    } finally {
      if (activeCheckDateRef.current === checkDate) {
        setIsLoadingNumbers(false);
      }
    }
  };

  const handleSelectCheckDate = (checkDate: string) => {
    activeCheckDateRef.current = checkDate;
    setSelectedCheckDate(checkDate);
    setIsDatePickerOpen(false);
    fetchCheckNumbers(checkDate);
  };

  useEffect(() => {
    const fetchCheckDates = async () => {
      setIsLoadingDates(true);

      try {
        const response = await getPayStubCheckDates();
        const data = response.data as CheckDatesResponse;
        const dates = [...(data.checkDates || [])].sort((a, b) =>
          b.localeCompare(a),
        );

        setCheckDates(dates);
        if (dates.length > 0) {
          handleSelectCheckDate(dates[0]);
        }
      } catch (error) {
        setMessageType("error");
        setMessage(
          error instanceof Error
            ? error.message
            : "Failed to load your pay dates.",
        );
      } finally {
        setIsLoadingDates(false);
      }
    };

    fetchCheckDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedCheckDates = useMemo(() => {
    const groups: Record<string, string[]> = {};
    checkDates.forEach((date) => {
      const year = date.slice(0, 4);
      if (!groups[year]) groups[year] = [];
      groups[year].push(date);
    });

    return Object.keys(groups)
      .sort((a, b) => Number(b) - Number(a))
      .map((year) => ({ year, dates: groups[year] }));
  }, [checkDates]);

  const handleDownload = async () => {
    if (!selectedCheckDate || !selectedCheckNumber) return;

    setIsRequesting(true);
    setMessage("");

    try {
      const { blob, fileName } = await requestTaxDocument(
        {
          documentType: "Paystub",
          checkDate: selectedCheckDate,
          checkNumber: selectedCheckNumber,
        },
        `Paystub-${selectedCheckDate}-${selectedCheckNumber}.pdf`,
      );
      downloadBlob(blob, fileName);
      setMessageType("success");
      setMessage("Your pay stub has been downloaded.");
    } catch (error) {
      setMessageType("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Failed to download your pay stub. Please try again.",
      );
    } finally {
      setIsRequesting(false);
    }
  };

  if (isLoadingDates) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {message && (
        <MessageTile isVisible={true} message={message} type={messageType} />
      )}

      {checkDates.length === 0 ? (
        <div className="p-4 text-muted-foreground bg-background border border-border rounded-lg">
          No pay stub history found.
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-w-sm">
          <div className="flex flex-col gap-2">
            <Label htmlFor="paystub-check-date">Pay Date</Label>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger
                id="paystub-check-date"
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "justify-between font-normal w-full",
                )}
              >
                {selectedCheckDate
                  ? formatCheckDate(selectedCheckDate)
                  : "Select a pay date"}
                <Icon icon="mdi:chevron-down" />
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]">
                <Command>
                  <CommandInput
                    className="p-2"
                    placeholder="Search pay dates..."
                  />
                  <CommandList>
                    <CommandEmpty>No pay dates found.</CommandEmpty>
                    {groupedCheckDates.map(({ year, dates }) => (
                      <CommandGroup key={year} heading={year}>
                        {dates.map((date) => (
                          <CommandItem
                            key={date}
                            value={formatCheckDate(date)}
                            onSelect={() => handleSelectCheckDate(date)}
                          >
                            {selectedCheckDate === date && (
                              <Icon icon="mdi:check" />
                            )}
                            {formatCheckDate(date)}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="paystub-check-number">Check Number</Label>
            {!isLoadingNumbers && checkNumbers.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground bg-background border border-border rounded-lg">
                No check numbers found for this pay date.
              </div>
            ) : (
              <Select
                value={selectedCheckNumber ?? ""}
                onValueChange={setSelectedCheckNumber}
                disabled={isLoadingNumbers || checkNumbers.length === 0}
              >
                <SelectTrigger id="paystub-check-number">
                  <SelectValue
                    placeholder={
                      isLoadingNumbers
                        ? "Loading check numbers..."
                        : "Select a check number"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {checkNumbers.map((checkNumber) => (
                    <SelectItem key={checkNumber} value={checkNumber}>
                      {checkNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={
              !selectedCheckDate ||
              !selectedCheckNumber ||
              isRequesting ||
              isLoadingNumbers
            }
          >
            <Icon icon="uiw:pay" />
            {isRequesting ? "Requesting..." : "Download Pay Stub"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MyPayStubs;
